import { QueryState, RequestAction, getQuerySelector } from '@redux-requests/core';
import { createAction as createSmartAction } from 'redux-smart-actions';
import { app, inspection } from 'store';
import { fetchReports } from 'store/report/requests';
import { CTFModuleTypes, DifferenceTypeNames, DocumentTypes } from 'types';
import PDFTronAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { PDFManagerFactory } from 'pdftron';

import { fetchGroups } from '../difference-group/actions';
import type { FetchGroupsData } from '../difference-group/types';
import { getGroups } from '../difference-group/selectors';
import {
  ApiBulkUpdateDifferences,
  ApiDeleteDifference,
  ApiFetchDifferences,
  ApiRestoreAllDifferences,
  Difference,
  FetchDifferencesData,
  formatRgb,
  formatCmyk,
  formatLab,
} from './types';

// Helper function to generate thumbnail paths consistently (uploadId => unique S3 key per upload for CDN cache busting)
const generateThumbnailPath = (
  inspectionId: string,
  documentType: string,
  differenceId: string,
  uploadId?: string,
): string => {
  const path =
    uploadId !== undefined && uploadId !== ''
      ? `verifyThumbnails/${inspectionId}/${documentType}/${differenceId}/${uploadId}.png`
      : `verifyThumbnails/${inspectionId}/${documentType}/${differenceId}.png`;
  const isAiDomain = window.location.hostname.endsWith('.ai');
  const cdnDomain = isAiDomain ? import.meta.env.VITE_VERIFY_CDN_AI_URL : import.meta.env.VITE_VERIFY_CDN_URL;
  console.log('cdnDomain', cdnDomain);
  if (!cdnDomain) {
    throw new Error('Environment variable VITE_VERIFY_CDN_URL is required but not defined.');
  }
  return window.location.hostname === 'localhost' ? `/cdn/${path}` : `${cdnDomain}/${path}`;
};

// Helper function to delete difference annotations (including color points)
const deleteDifferenceAnnotations = (differenceId: string): void => {
  const sourceManager = PDFTronAnnotationManager.getInstance(DocumentTypes.source);
  const targetManager = PDFTronAnnotationManager.getInstance(DocumentTypes.target);

  if (!sourceManager || !targetManager) return;

  [sourceManager, targetManager].forEach((manager) => {
    // Try to delete by ID first (works for most difference types)
    const annotationToDelete = manager.getAnnotationById(differenceId);
    if (annotationToDelete) {
      manager.deleteAnnotation(annotationToDelete, { imported: true, force: true });
    }
  });
};
import store, { axiosInstance } from 'store/store';
type fetchDifferencesPayload = [
  string, // inspection id
  boolean?, // optional parameter to track inspection start event
];

export const fetchDifferences = createSmartAction<
  RequestAction<ApiFetchDifferences, FetchDifferencesData>,
  fetchDifferencesPayload
>('differences/fetchDifferences', (id, shouldTrackInspection) => ({
  request: {
    url: `/inspections/${id}/differences`,
    method: 'GET',
  },
  meta: {
    errorMessage: 'There was an error loading the results. Please try again here',
    onSuccess: (response: QueryState<ApiFetchDifferences>, _action, store) => {
      store.dispatch(inspection.actions.setDifferencesLoaded(true));
      store.dispatch(fetchReports(id));
      const hasMarkups = (response.data ?? []).some((diff) => diff.type === DifferenceTypeNames.Markup);
      store.dispatch(app.actions.setShowMarkupTab(hasMarkups));
      return response;
    },
    shouldTrackInspection: Boolean(shouldTrackInspection),
  },
}));

type restoreAllDifferencePayload = [
  string, // inspection id'
  CTFModuleTypes[], // modules
];
export const restoreAllDifferences = createSmartAction<
  RequestAction<ApiRestoreAllDifferences>,
  restoreAllDifferencePayload
>('differences/restoreAllDifferences', (id, modules) => ({
  request: {
    url: `/inspections/${id}/differences/restore`,
    method: 'PATCH',
    data: { modules },
  },
  meta: {
    errorMessage: 'There was an error restoring differences. Please try again.',
    asMutation: true,
    mutations: {
      [fetchDifferences.toString()]: (_data: ApiFetchDifferences, mutationData: ApiRestoreAllDifferences) => {
        return mutationData;
      },
    },
  },
}));

export interface UpdateDifferenceData {
  comment?: string;
  viewed?: boolean;
  deletionMarkPosition?: { x: number; y: number; pageHeight: number; pageNumber: number };
  childList?: string[];
  updatedAt?: string;
  smartDiscard?: boolean;
  statusId?: string | null;
  thumbnailPaths?: {
    [documentType: string]: {
      thumbnailUrl: string | undefined | null; // undefined = loading state, null = failed
    };
  };
  selectedClosestColorIndex?: number;
}

const hasStoredThumbnailUrl = (url: string | null | undefined): url is string =>
  typeof url === 'string' && url.length > 0;

// Server responses include the full thumbnailPaths object, including empty `{}` placeholders for
// a side this request did not write. Those must not replace a URL already in local state.
const mergeThumbnailPathsFromServer = (
  existing: UpdateDifferenceData['thumbnailPaths'],
  incoming: UpdateDifferenceData['thumbnailPaths'],
): UpdateDifferenceData['thumbnailPaths'] => {
  if (!incoming) {
    return existing;
  }

  const merged = { ...existing };
  for (const [documentType, next] of Object.entries(incoming)) {
    const nextUrl = next?.thumbnailUrl;
    const currentUrl = existing?.[documentType]?.thumbnailUrl;
    if (nextUrl === undefined && hasStoredThumbnailUrl(currentUrl)) {
      continue;
    }
    merged[documentType] = { ...existing?.[documentType], ...next };
  }
  return merged;
};

type updateDifferencePayload = [
  string, // id
  UpdateDifferenceData, // patchData
  string?, // optional requestkey
];
export const updateDifference = createSmartAction<RequestAction<UpdateDifferenceData>, updateDifferencePayload>(
  'differences/updateDifference',
  (id, updateData, requestKey) => ({
    request: {
      url: `/differences/${id}`,
      method: 'PATCH',
      data: updateData,
    },
    meta: {
      requestKey: requestKey || id,
      // keeps pending and error state, no data
      errorMessage: 'There was an error updating the difference. Please try again.',
      asMutation: true,
      mutations: {
        [fetchDifferences.toString()]: {
          updateData: (data: FetchDifferencesData, mutationData: Difference): FetchDifferencesData => {
            if (!data) return data;

            return data.map((difference) => {
              let updatedDifference = difference;
              if (difference.id === id) {
                updatedDifference = {
                  ...difference,
                  comment: mutationData.comment ?? difference.comment,
                  viewed: true, // Always set to true on any update
                  updatedAt: new Date().toString(),
                  // Merge per document type. Source and target thumbnails upload concurrently and
                  // each write carries only its own side, so replacing the whole object here would
                  // drop the other side whenever responses arrive out of order.
                  thumbnailPaths: mutationData.thumbnailPaths
                    ? mergeThumbnailPathsFromServer(difference.thumbnailPaths, mutationData.thumbnailPaths)
                    : difference.thumbnailPaths,
                  target: mutationData.target !== undefined ? { ...mutationData.target } : difference.target,
                  source: mutationData.source !== undefined ? { ...mutationData.source } : difference.source,
                  embeddedImageDetails: mutationData.embeddedImageDetails
                    ? { ...mutationData.embeddedImageDetails }
                    : difference.embeddedImageDetails,
                  colorDetails: mutationData.colorDetails ?? difference.colorDetails,
                };
              }
              return updatedDifference;
            });
          },
          updateDataOptimistic: (data: FetchDifferencesData) => {
            if (!data) return data;
            return data.map((difference: Difference) => {
              if (difference.id === id) {
                const { selectedClosestColorIndex, thumbnailPaths, ...restUpdateData } = updateData;
                const colorDetailsUpdate =
                  selectedClosestColorIndex !== undefined && difference.colorDetails
                    ? (() => {
                        const closest = difference.colorDetails.closestColors?.[selectedClosestColorIndex];
                        return {
                          colorDetails: {
                            ...difference.colorDetails,
                            selectedClosestColorIndex,
                            ...(closest
                              ? {
                                  closestRgb: formatRgb(closest.color.rgb),
                                  closestCmyk: formatCmyk(closest.color.cmyk),
                                  closestLab: formatLab(closest.color.lab),
                                  deltaE: String(closest.deltaE),
                                }
                              : {
                                  closestRgb: '',
                                  closestCmyk: '',
                                  closestLab: '',
                                  deltaE: '',
                                }),
                          },
                        };
                      })()
                    : {};
                return {
                  ...difference,
                  ...restUpdateData,
                  ...colorDetailsUpdate,
                  // Merged per document type for the same reason as the success handler above.
                  ...(thumbnailPaths ? { thumbnailPaths: { ...difference.thumbnailPaths, ...thumbnailPaths } } : {}),
                  viewed: true,
                  updatedAt: new Date().toString(),
                };
              }
              return difference;
            });
          },
        },
      },
    },
  }),
);

export interface BulkUpdateData {
  statusUpdated?: {
    differenceIds: string[];
    groupIds: string[];
    statusId: string | null;
  };
  commentUpdated?: {
    differenceIds: string[];
    groupIds: string[];
    comment: string;
  };
}

export interface CreateMergedDifferenceData {
  inspectionId: string;
  originalDifferenceIds: string[];
  sourceLocation: {
    pageNumber: number;
    rect: number[];
  };
  targetLocation: {
    pageNumber: number;
    rect: number[];
  };
  comment?: string;
}

export const createMergedDifference = createSmartAction<RequestAction<Difference>, [CreateMergedDifferenceData]>(
  'differences/createMergedDifference',
  (mergeData) => ({
    request: {
      url: '/differences/merge',
      method: 'POST',
      data: mergeData,
    },
    meta: {
      requestKey: 'create-merged-difference',
      errorMessage: 'There was an error creating the merged difference. Please try again.',
      asMutation: true,
      mutations: {
        [fetchDifferences.toString()]: {
          updateData: (data: FetchDifferencesData, mutationData: Difference): FetchDifferencesData => {
            if (!data) return data;

            // Remove the original differences that were merged
            const filteredData = data.filter((difference) => !mergeData.originalDifferenceIds.includes(difference.id));

            // Add the new merged difference
            return [...filteredData, mutationData];
          },
        },
      },
      onSuccess: (response: QueryState<Difference>, _action, store) => {
        // Note: Focus is handled by the saga after annotations are properly drawn

        store.dispatch(
          app.actions.setSnackMessage({
            message: `Successfully merged ${mergeData.originalDifferenceIds.length} graphics differences`,
            type: 'success',
          }),
        );

        return response;
      },
    },
  }),
);

export const unmergeDifference = createSmartAction<RequestAction<Difference[]>, [string, string]>(
  'differences/unmergeDifference',
  (mergedDifferenceId, inspectionId) => ({
    request: {
      url: `/differences/${mergedDifferenceId}/unmerge`,
      method: 'POST',
      data: { inspectionId },
    },
    meta: {
      requestKey: 'unmerge-difference',
      errorMessage: 'There was an error unmerging the difference. Please try again.',
      asMutation: true,
      mergedDifferenceId, // Pass for saga cleanup
      mutations: {
        [fetchDifferences.toString()]: {
          updateData: (data: FetchDifferencesData, mutationData: Difference[]): FetchDifferencesData => {
            if (!data || !mutationData) return data;

            // Remove the merged difference
            const filteredData = data.filter((difference) => difference.id !== mergedDifferenceId);

            // Add back the restored original differences
            return [...filteredData, ...mutationData];
          },
        },
      },
      onSuccess: (response: QueryState<Difference[]>, _action, store) => {
        const restoredCount = response.data?.length || 0;

        store.dispatch(
          app.actions.setSnackMessage({
            message: `Successfully unmerged and restored ${restoredCount} graphics differences`,
            type: 'success',
          }),
        );

        return response;
      },
    },
  }),
);

export const bulkUpdateDifferences = createSmartAction<RequestAction<BulkUpdateData>, [BulkUpdateData]>(
  'differences/bulkUpdateDifferences',
  (updateData) => ({
    request: {
      url: '/differences/bulk-update',
      method: 'PATCH',
      data: updateData,
    },
    meta: {
      requestKey: 'bulk-update',
      errorMessage: 'There was an error updating the differences. Please try again.',
      asMutation: true,
      onSuccess: (response: QueryState<ApiBulkUpdateDifferences>, action, store) => {
        const totalUpdated = (response.data?.differences?.length || 0) + (response.data?.groups?.length || 0);
        if (totalUpdated > 0) {
          store.dispatch(
            app.actions.setSnackMessage({
              message: `Edits were successfully applied to ${totalUpdated} results`,
              type: 'success',
            }),
          );
        } else {
          store.dispatch(
            app.actions.setSnackMessage({
              message: 'Oops! The edits failed and could not be applied. Please try again.',
              type: 'error',
            }),
          );
        }

        // Handle persisted comments for bulk updates
        const requestData = action.request.data as BulkUpdateData;
        if (requestData.commentUpdated) {
          const { differenceIds, groupIds, comment } = requestData.commentUpdated;
          const affectedDiffIds = [...differenceIds];

          // Get differences that belong to updated groups
          if (groupIds.length > 0) {
            const state = store.getState();
            const groups = getGroups(state);

            const groupDiffIds = groupIds.flatMap((groupId) => {
              const group = groups[groupId];
              return group ? group.differenceList : [];
            });

            affectedDiffIds.push(...groupDiffIds);
          }

          if (affectedDiffIds.length > 0) {
            store.dispatch(
              inspection.actions.setPersistedComment({
                diffIds: affectedDiffIds,
                comment: comment,
              }),
            );
          }
        }

        return response;
      },
      onError: (_error, _action, store) => {
        store.dispatch(
          app.actions.setSnackMessage({
            message: 'Oops! The edits failed and could not be applied. Please try again.',
            type: 'error',
          }),
        );
      },
      mutations: {
        [fetchGroups.toString()]: {
          updateData: (groupsData: FetchGroupsData, mutationData: ApiBulkUpdateDifferences): FetchGroupsData => {
            if (!groupsData) return groupsData;

            const failedGroupIds = new Set(mutationData.failed.groups);
            const updatedGroups = { ...groupsData };

            // Update existing groups with new data
            mutationData.groups.forEach((groupData) => {
              if (!failedGroupIds.has(groupData.id) && updatedGroups[groupData.id]) {
                updatedGroups[groupData.id] = {
                  ...updatedGroups[groupData.id],
                  ...groupData,
                  updatedAt: new Date().toString(),
                };
              }
            });

            return updatedGroups;
          },
        },
        [fetchDifferences.toString()]: {
          updateData: (data: FetchDifferencesData, mutationData: ApiBulkUpdateDifferences): FetchDifferencesData => {
            if (!data) return data;

            const failedIds = new Set(mutationData.failed.differences);
            const failedGroupIds = new Set(mutationData.failed.groups);

            // Create a map of updated groups for quick lookup
            const updatedGroupsMap = new Map(
              mutationData.groups.filter((group) => !failedGroupIds.has(group.id)).map((group) => [group.id, group]),
            );

            return data.map((difference) => {
              if (failedIds.has(difference.id)) {
                return difference;
              }

              // First check for difference updates
              const updatedDiff = mutationData.differences.find((d) => d.id === difference.id);
              if (updatedDiff) {
                return {
                  ...difference,
                  ...updatedDiff,
                  updatedAt: new Date().toString(),
                };
              }

              // Then check if this difference belongs to an updated group
              if (difference.groupId && updatedGroupsMap.has(difference.groupId)) {
                const updatedGroup = updatedGroupsMap.get(difference.groupId)!;

                return {
                  ...difference,
                  comment: updatedGroup.comment ?? difference.comment,
                  statusId: updatedGroup.statusId ?? difference.statusId,
                  viewed: updatedGroup.viewed ?? difference.viewed,
                  updatedAt: new Date().toString(),
                };
              }

              return difference;
            });
          },
          updateDataOptimistic: (data: FetchDifferencesData) => {
            if (!data) return data;

            const updatedDifferences = [...data];
            const updatedDiffIds = new Set([
              ...(updateData.statusUpdated?.differenceIds || []),
              ...(updateData.commentUpdated?.differenceIds || []),
            ]);
            const updatedGroupIds = new Set([
              ...(updateData.statusUpdated?.groupIds || []),
              ...(updateData.commentUpdated?.groupIds || []),
            ]);

            return updatedDifferences.map((difference) => {
              // Handle direct difference updates
              if (updatedDiffIds.has(difference.id)) {
                const updates: Partial<Difference> = {};

                if (updateData.statusUpdated?.differenceIds.includes(difference.id)) {
                  updates.statusId = updateData.statusUpdated.statusId;
                }

                if (updateData.commentUpdated?.differenceIds.includes(difference.id)) {
                  updates.comment = updateData.commentUpdated.comment;
                }

                return {
                  ...difference,
                  ...updates,
                  updatedAt: new Date().toString(),
                };
              }

              // Handle updates via group membership
              if (difference.groupId && updatedGroupIds.has(difference.groupId)) {
                const updates: Partial<Difference> = {};

                if (updateData.statusUpdated?.groupIds.includes(difference.groupId)) {
                  updates.statusId = updateData.statusUpdated.statusId;
                }

                if (updateData.commentUpdated?.groupIds.includes(difference.groupId)) {
                  updates.comment = updateData.commentUpdated.comment;
                }

                return {
                  ...difference,
                  ...updates,
                  updatedAt: new Date().toString(),
                };
              }

              return difference;
            });
          },
        },
      },
    },
  }),
);

export const createMarkupDifference = createSmartAction<RequestAction<Difference>, [string, string, any, any]>(
  'differences/markup',
  (inspectionId, markupId, differenceDetails, clientDifference: Difference) => {
    // Generate thumbnail paths on the frontend
    const thumbnailPaths: { [documentType: string]: { thumbnailUrl: string | undefined } } = {};

    // Generate paths for master (source) and sample (target) if they exist
    if (differenceDetails.master) {
      thumbnailPaths.source = {
        thumbnailUrl: generateThumbnailPath(inspectionId, 'master', markupId),
      };
    }

    if (differenceDetails.sample) {
      thumbnailPaths.target = {
        thumbnailUrl: generateThumbnailPath(inspectionId, 'sample', markupId),
      };
    }

    // Add thumbnailPaths to differenceDetails
    const enhancedDifferenceDetails = {
      ...differenceDetails,
      thumbnailPaths,
    };

    return {
      request: {
        url: '/differences/markup',
        method: 'POST',
        data: { inspectionId, markupId, differenceDetails: enhancedDifferenceDetails },
      },
      meta: {
        asMutation: true,
        errorMessage: 'There was an error linking the markup. Please try again.',
        mutations: {
          [fetchDifferences.toString()]: {
            updateDataOptimistic: (data: FetchDifferencesData) => {
              if (!data) return data;
              const newDifference = {
                ...clientDifference,
                sourceThumbnailLoading: true,
                targetThumbnailLoading: true,
              };
              return [newDifference, ...data];
            },
          },
        },
      },
    };
  },
);

export const linkMarkupDifference = createSmartAction<RequestAction<Difference>, [string, DocumentTypes, any]>(
  'differences/link-markup',
  (differenceId, documentType, document) => ({
    request: {
      url: `/differences/${differenceId}/link-markup`,
      method: 'PATCH',
      data: { documentType, document },
    },
    meta: {
      asMutation: true,
      mutations: {
        [fetchDifferences.toString()]: {
          updateData: (data: FetchDifferencesData, mutationData: Difference): FetchDifferencesData => {
            if (!data) return data;
            return data.map((difference) => {
              if (difference.id === differenceId) {
                return {
                  ...difference,
                  ...mutationData,
                  viewed: true,
                  updatedAt: new Date().toString(),
                };
              }
              return difference;
            });
          },
          updateDataOptimistic: (data: FetchDifferencesData) => {
            if (!data) return data;
            return data.map((difference: Difference) => {
              if (difference.id === differenceId) {
                try {
                  // Safely convert boundingBox to rect format for frontend
                  const boundingBox = document?.location?.boundingBox;
                  if (!boundingBox || !document?.location?.pageNumber) {
                    console.error('Invalid document structure in optimistic update:', document);
                    return difference; // Return unchanged if invalid
                  }

                  const rect = [boundingBox.left, boundingBox.top, boundingBox.right, boundingBox.bottom];

                  return {
                    ...difference,
                    [documentType]: {
                      ...difference[documentType],
                      location: {
                        pageNumber: document.location.pageNumber,
                        rect: rect,
                        rotationIndex: document.location.rotationIndex,
                      },
                    },
                    viewed: true,
                    updatedAt: new Date().toString(),
                  };
                } catch (error) {
                  console.error('Error in optimistic update:', error);
                  return difference; // Return unchanged if error
                }
              }
              return difference;
            });
          },
        },
      },
    },
  }),
);

export const linkEmbeddedImageMatch = createSmartAction<RequestAction<Difference>, [string, DocumentTypes, any]>(
  'differences/link-embedded-image',
  (differenceId, documentType, document) => ({
    request: {
      url: `/differences/${differenceId}/link-embedded-image`,
      method: 'PATCH',
      data: { documentType, document },
    },
    meta: {
      asMutation: true,
      mutations: {
        [fetchDifferences.toString()]: {
          updateData: (data: FetchDifferencesData, mutationData: Difference): FetchDifferencesData => {
            if (!data) return data;
            return data.map((difference) => {
              if (difference.id === differenceId) {
                return {
                  ...difference,
                  ...mutationData,
                  viewed: true,
                  updatedAt: new Date().toString(),
                };
              }
              return difference;
            });
          },
          updateDataOptimistic: (data: FetchDifferencesData) => {
            if (!data) return data;
            return data.map((difference: Difference) => {
              if (difference.id === differenceId) {
                try {
                  const boundingBox = document?.location?.boundingBox;
                  if (!boundingBox || !document?.location?.pageNumber) {
                    return difference;
                  }

                  const x = boundingBox.left;
                  const y = Math.min(boundingBox.top, boundingBox.bottom);
                  const width = boundingBox.right - x;
                  const height = Math.max(boundingBox.bottom, boundingBox.top) - y;
                  const rect = [x, y, width, height];
                  const newLocation = {
                    pageNumber: document.location.pageNumber,
                    boundingBox,
                  };

                  const matchLocationKey = documentType === 'source' ? 'masterLocation' : 'sampleLocation';
                  const matchDetails = difference.embeddedImageDetails?.matchDetails?.[0];

                  const locationFromRect = (loc: { pageNumber: number; rect: number[] }) => {
                    const [x, y, w, h] = loc.rect;
                    return {
                      pageNumber: loc.pageNumber,
                      boundingBox: {
                        left: x,
                        top: y,
                        right: x + w,
                        bottom: y + h,
                      },
                    };
                  };

                  const matchDetailsNext = matchDetails
                    ? [{ ...matchDetails, confidenceLevel: 'High', [matchLocationKey]: newLocation }]
                    : [
                        {
                          confidence: 1,
                          confidenceLevel: 'High' as const,
                          masterLocation:
                            documentType === 'source' ? newLocation : locationFromRect(difference.source.location),
                          sampleLocation:
                            documentType === 'target' ? newLocation : locationFromRect(difference.target.location),
                        },
                      ];

                  return {
                    ...difference,
                    [documentType]: {
                      ...difference[documentType],
                      location: {
                        pageNumber: document.location.pageNumber,
                        rect,
                      },
                    },
                    embeddedImageDetails: {
                      ...difference.embeddedImageDetails,
                      userLinkedMatch: true,
                      matchDetails: matchDetailsNext,
                    },
                    viewed: true,
                    updatedAt: new Date().toString(),
                  };
                } catch (error) {
                  console.error('Error in optimistic update:', error);
                  return difference;
                }
              }
              return difference;
            });
          },
        },
      },
    },
  }),
);

export const unlinkEmbeddedImageMatch = createSmartAction<RequestAction<Difference>, [string]>(
  'differences/unlink-embedded-image',
  (differenceId) => ({
    request: {
      url: `/differences/${differenceId}/unlink-embedded-image`,
      method: 'PATCH',
    },
    meta: {
      differenceId,
      asMutation: true,
      mutations: {
        [fetchDifferences.toString()]: {
          updateData: (data: FetchDifferencesData, mutationData: Difference): FetchDifferencesData => {
            if (!data) return data;
            return data.map((difference) => {
              if (difference.id === differenceId) {
                return {
                  ...difference,
                  ...mutationData,
                  updatedAt: new Date().toString(),
                };
              }
              return difference;
            });
          },
          updateDataOptimistic: (data: FetchDifferencesData) => {
            if (!data) return data;
            return data.map((difference: Difference) => {
              if (difference.id === differenceId) {
                try {
                  let side = difference.embeddedImageDetails?.deletionMarkerTarget as 'source' | 'target' | undefined;
                  if (side !== 'source' && side !== 'target') {
                    const srcUrl = difference.thumbnailPaths?.source?.thumbnailUrl;
                    const tgtUrl = difference.thumbnailPaths?.target?.thumbnailUrl;
                    if (typeof srcUrl === 'string' && srcUrl.length > 0 && !tgtUrl) {
                      side = 'source';
                    } else if (typeof tgtUrl === 'string' && tgtUrl.length > 0 && !srcUrl) {
                      side = 'target';
                    }
                  }

                  const zeroRect = [0, 0, 0, 0];
                  const zeroBoundingBox = { top: 0, left: 0, right: 0, bottom: 0 };
                  const zeroLocation = { pageNumber: 1, boundingBox: zeroBoundingBox };

                  const matchDetails = difference.embeddedImageDetails?.matchDetails?.[0];
                  const matchDetailsNext = matchDetails
                    ? [
                        {
                          ...matchDetails,
                          confidenceLevel: 'None',
                          ...(side === 'source' ? { masterLocation: zeroLocation } : {}),
                          ...(side === 'target' ? { sampleLocation: zeroLocation } : {}),
                        },
                      ]
                    : difference.embeddedImageDetails?.matchDetails;

                  const updatedDifference: Difference = {
                    ...difference,
                    embeddedImageDetails: {
                      ...difference.embeddedImageDetails,
                      userLinkedMatch: undefined,
                      matchDetails: matchDetailsNext,
                    },
                    updatedAt: new Date().toString(),
                  };

                  if (side === 'source') {
                    updatedDifference.source = {
                      ...difference.source,
                      location: { ...difference.source.location, pageNumber: 1, rect: zeroRect },
                    };
                  } else if (side === 'target') {
                    updatedDifference.target = {
                      ...difference.target,
                      location: { ...difference.target.location, pageNumber: 1, rect: zeroRect },
                    };
                  }

                  delete updatedDifference.thumbnailPaths;

                  return updatedDifference;
                } catch (error) {
                  console.error('Error in optimistic unlink update:', error);
                  return difference;
                }
              }
              return difference;
            });
          },
        },
      },
    },
  }),
);

export const startThumbnailUploadMarkup = createSmartAction<
  // thumbnail upload for markups/merge grpahics do same thing, it should be consolidated to one
  RequestAction<Difference>,
  [string, string, any, DocumentTypes]
>('differences/thumbnail-presigned-url', (inspectionId, differenceId, thumbnailBlob, documentType) => {
  const uploadId = crypto.randomUUID();
  return {
    request: {
      url: `/inspections/${inspectionId}/thumbnail-presigned-url`,
      method: 'POST',
      data: { differenceId, documentType, uploadId },
      withCredentials: true,
    },
    meta: {
      requestKey: `differenceId-${documentType}`,
      onSuccess: async (response) => {
        const url = response.data;

        try {
          await axiosInstance.put(url, thumbnailBlob, {
            headers: {
              'Content-Type': 'image/png',
            },
          });

          const thumbnailPath = generateThumbnailPath(inspectionId, documentType, differenceId, uploadId);

          // Add a delay to ensure CloudFront cookies are processed by the browser
          // before the component tries to load the CDN image
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Write only the side that was just uploaded. Both sides upload concurrently, so a
          // whole-object snapshot could carry a blank value for the other side and overwrite it.
          store.dispatch(
            updateDifference(differenceId, {
              thumbnailPaths: { [documentType]: { thumbnailUrl: thumbnailPath } },
            }),
          );

          // Check if this difference is currently focused and both thumbnails are now ready
          // If so, trigger the animation immediately. Read back after dispatching so both sides
          // reflect the merged state rather than this upload's own snapshot.
          const currentState = store.getState();
          const currentDifference = (
            getQuerySelector({ type: fetchDifferences.toString() })(currentState)?.data || []
          ).find((diff: Difference) => diff.id === differenceId);
          const focusedDifferenceId = currentState.inspection.focusedDifferenceId;
          const hasSourceThumbnail = currentDifference?.thumbnailPaths?.source?.thumbnailUrl;
          const hasTargetThumbnail = currentDifference?.thumbnailPaths?.target?.thumbnailUrl;
          const flashMode = currentState.inspection.flashMode;

          if (focusedDifferenceId === differenceId && hasSourceThumbnail && hasTargetThumbnail && flashMode) {
            const docManager = PDFManagerFactory.getPDFDocManager();
            if (docManager) {
              // Delay to ensure state is fully updated and images are accessible
              setTimeout(() => {
                const freshState = store.getState();
                const currentFocusedId = freshState.inspection.focusedDifferenceId;

                // Only animate if this difference is still focused
                if (currentFocusedId !== differenceId) {
                  return;
                }

                const differences = getQuerySelector({ type: fetchDifferences.toString() })(freshState)?.data || [];
                const updatedDifference = differences.find((diff: Difference) => diff.id === differenceId);

                if (updatedDifference) {
                  docManager.animateMarkup(updatedDifference);
                }
              }, 200);
            }
          }
        } catch (uploadError) {
          console.error('S3 thumbnail upload failed:', uploadError);
        }

        return response;
      },
      mutations: {
        [fetchDifferences.toString()]: {
          updateData: (data: FetchDifferencesData): FetchDifferencesData => {
            if (!data) return data;
            return data.map((difference) => {
              if (difference.id === differenceId) {
                return {
                  ...difference,
                  [documentType === DocumentTypes.source ? 'sourceThumbnailLoading' : 'targetThumbnailLoading']: false,
                };
              }
              return difference;
            });
          },
        },
      },
    },
  };
});

export const startThumbnailUpload = createSmartAction<RequestAction<Difference>, [string, string, any, DocumentTypes]>(
  'differences/thumbnail-presigned-url',
  (inspectionId, differenceId, thumbnailBlob, documentType) => {
    // Map DocumentTypes to API expected format
    const documentTypeMapping = {
      [DocumentTypes.source]: 'master',
      [DocumentTypes.target]: 'sample',
    };
    const apiDocumentType = documentTypeMapping[documentType];
    const uploadId = crypto.randomUUID();

    return {
      request: {
        url: `/inspections/${inspectionId}/thumbnail-presigned-url`,
        method: 'POST',
        data: { differenceId, documentType: apiDocumentType, uploadId },
        withCredentials: true, // Re-enabled - backend now handles CORS properly
      },
      meta: {
        onSuccess: async (response) => {
          const url = response.data;

          try {
            await axiosInstance.put(url, thumbnailBlob, {
              headers: {
                'Content-Type': 'image/png',
              },
            });

            const thumbnailPath = generateThumbnailPath(inspectionId, apiDocumentType, differenceId, uploadId);

            // Add a delay to ensure CloudFront cookies are processed by the browser
            // before the component tries to load the CDN image
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Write only the side that was just uploaded. Both sides upload concurrently, so a
            // whole-object snapshot could carry a blank value for the other side and overwrite it.
            store.dispatch(
              updateDifference(differenceId, {
                thumbnailPaths: { [documentType]: { thumbnailUrl: thumbnailPath } },
              }),
            );
          } catch (uploadError) {
            console.error('S3 thumbnail upload failed:', uploadError);
          }

          return response;
        },
        onError: (error) => {
          console.error('Failed to get presigned URL for thumbnail:', error);
        },
      },
    };
  },
);
export const deleteMarkup = createSmartAction<RequestAction<ApiDeleteDifference>, [string]>(
  'differences/deleteMarkup',
  (id) => ({
    request: {
      url: `/differences/${id}`,
      method: 'DELETE',
    },
    meta: {
      errorMessage: 'There was an error deleting the difference. Please try again.',
      asMutation: true,
      mutations: {
        [fetchDifferences.toString()]: {
          updateDataOptimistic: (differences: FetchDifferencesData) => {
            const filteredDifferences = differences.filter((difference) => difference.id !== id);
            return filteredDifferences;
          },
        },
      },
      onRequest: (req) => {
        const sourceAnnoManager = PDFTronAnnotationManager.getInstance(DocumentTypes.source);
        const targetAnnoManager = PDFTronAnnotationManager.getInstance(DocumentTypes.target);

        if (!sourceAnnoManager || !targetAnnoManager) {
          return req;
        }

        // Check if the markup being deleted is currently focused and unfocus it
        const currentState = store.getState();
        const focusedDifferenceId = currentState.inspection.focusedDifferenceId;
        if (focusedDifferenceId === id) {
          store.dispatch(inspection.actions.unfocusDifference());
        }

        // Check if the markup being deleted is the one used in comparison mode and reset if so
        const pdfDocManager = PDFManagerFactory.getPDFDocManager();
        if (pdfDocManager && pdfDocManager.originalMarkupId === id && pdfDocManager.markupMode === 'comparison') {
          pdfDocManager.resetMarkupMode();
        }

        // Deselect annotations before deleting them
        sourceAnnoManager.deselectAllAnnotations();
        targetAnnoManager.deselectAllAnnotations();

        // Delete the annotations
        deleteDifferenceAnnotations(id);

        return req;
      },
      onSuccess: (req) => {
        store.dispatch(
          app.actions.setSnackMessage({
            message: 'Markup was successfully deleted.',
            type: 'success',
          }),
        );
        // Optimistic mutation already removed the markup — hide the tab if none remain.
        // Use getQuerySelector directly to avoid a circular import with ./selectors.
        const queryState = getQuerySelector({ type: fetchDifferences })(store.getState());
        const remaining = (queryState.data ?? []) as FetchDifferencesData;
        const hasMarkupsLeft = remaining.some((diff) => diff.type === DifferenceTypeNames.Markup);
        if (!hasMarkupsLeft) {
          store.dispatch(app.actions.setShowMarkupTab(false));
        }
        return req;
      },
    },
  }),
);

export const deleteColor = createSmartAction<RequestAction<ApiDeleteDifference>, [string]>(
  'differences/deleteColor',
  (id) => ({
    request: {
      url: `/differences/${id}`,
      method: 'DELETE',
    },
    meta: {
      errorMessage: 'There was an error deleting the color difference. Please try again.',
      asMutation: true,
      mutations: {
        [fetchDifferences.toString()]: {
          updateDataOptimistic: (differences: FetchDifferencesData) =>
            differences.filter((difference) => difference.id !== id),
        },
      },
      onRequest: (req) => {
        const sourceAnnoManager = PDFTronAnnotationManager.getInstance(DocumentTypes.source);
        const targetAnnoManager = PDFTronAnnotationManager.getInstance(DocumentTypes.target);

        if (!sourceAnnoManager || !targetAnnoManager) {
          return req;
        }

        if (store.getState().inspection.focusedDifferenceId === id) {
          store.dispatch(inspection.actions.unfocusDifference());
        }

        [
          { manager: sourceAnnoManager, annotations: sourceAnnoManager.getAnnotationsList() },
          { manager: targetAnnoManager, annotations: targetAnnoManager.getAnnotationsList() },
        ].forEach(({ manager, annotations }) => {
          manager.deselectAllAnnotations();
          // Color differences use DifferenceAnnotation objects where Id is the difference ID
          const colorDiffAnnotation = annotations.find((annot: any) => annot.Id === id);
          if (colorDiffAnnotation) {
            manager.deleteAnnotation(colorDiffAnnotation, { imported: true, force: true });
          }
        });

        return req;
      },
      onSuccess: (req) => {
        store.dispatch(
          app.actions.setSnackMessage({
            message: 'Color difference was successfully deleted.',
            type: 'success',
          }),
        );
        return req;
      },
    },
  }),
);
